// =============================================================================
// Terminal Manager - 终端管理组件
// =============================================================================

import React, { useState, useEffect } from 'react';
import {
  Terminal,
  Trash2,
  Square,
  Play,
  BarChart3,
  RefreshCw,
  AlertTriangle,
  CheckSquare,
  X,
  Filter,
  Download
} from 'lucide-react';
import TerminalTypeIndicator from './TerminalTypeIndicator';
import ScriptExecutionMonitor from './ScriptExecutionMonitor';
import toast from 'react-hot-toast';

export default function TerminalManager({ 
  workspaceId, 
  onTerminalSelect,
  onTerminalDelete,
  compact = false 
}) {
  const [terminals, setTerminals] = useState([]);
  const [selectedTerminals, setSelectedTerminals] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, inactive, by-type
  const [showStats, setShowStats] = useState(false);
  const [resourceStats, setResourceStats] = useState(null);
  const [showBatchActions, setShowBatchActions] = useState(false);

  useEffect(() => {
    if (workspaceId) {
      loadTerminals();
      loadResourceStats();
      
      // 定期刷新
      const interval = setInterval(() => {
        loadTerminals();
        loadResourceStats();
      }, 10000);
      
      return () => clearInterval(interval);
    }
  }, [workspaceId]);

  const loadTerminals = async () => {
    try {
      const response = await fetch(`/api/terminals/workspace/${workspaceId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setTerminals(data.data);
        }
      }
    } catch (error) {
      console.error('Failed to load terminals:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadResourceStats = async () => {
    try {
      const response = await fetch('/api/terminals/stats/resources', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setResourceStats(data.data.stats);
        }
      }
    } catch (error) {
      console.error('Failed to load resource stats:', error);
    }
  };

  const handleDeleteTerminal = async (terminalId, force = false) => {
    if (!window.confirm('确定要删除这个终端吗？')) {
      return;
    }

    try {
      const response = await fetch(`/api/terminals/${terminalId}?force=${force}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.ok) {
        toast.success('终端已删除');
        loadTerminals();
        if (onTerminalDelete) {
          onTerminalDelete(terminalId);
        }
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete terminal');
      }
    } catch (error) {
      toast.error(`删除失败: ${error.message}`);
    }
  };

  const handleBatchDelete = async (force = false) => {
    if (selectedTerminals.size === 0) {
      toast.error('请先选择要删除的终端');
      return;
    }

    const terminalIds = Array.from(selectedTerminals);
    if (!window.confirm(`确定要删除 ${terminalIds.length} 个终端吗？`)) {
      return;
    }

    try {
      const response = await fetch('/api/terminals', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          terminalIds,
          force
        })
      });

      if (response.ok) {
        const data = await response.json();
        const { success, failed } = data.data;
        
        if (success > 0) {
          toast.success(`成功删除 ${success} 个终端`);
        }
        if (failed > 0) {
          toast.error(`${failed} 个终端删除失败`);
        }
        
        setSelectedTerminals(new Set());
        loadTerminals();
        
        if (onTerminalDelete) {
          terminalIds.forEach(id => onTerminalDelete(id));
        }
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Batch delete failed');
      }
    } catch (error) {
      toast.error(`批量删除失败: ${error.message}`);
    }
  };

  const toggleTerminalSelection = (terminalId) => {
    const newSelected = new Set(selectedTerminals);
    if (newSelected.has(terminalId)) {
      newSelected.delete(terminalId);
    } else {
      newSelected.add(terminalId);
    }
    setSelectedTerminals(newSelected);
  };

  const selectAllTerminals = () => {
    const filteredTerminals = getFilteredTerminals();
    setSelectedTerminals(new Set(filteredTerminals.map(t => t.id)));
  };

  const clearSelection = () => {
    setSelectedTerminals(new Set());
  };

  const getFilteredTerminals = () => {
    let filtered = terminals;

    switch (filter) {
      case 'active':
        filtered = terminals.filter(t => t.isActive);
        break;
      case 'inactive':
        filtered = terminals.filter(t => !t.isActive);
        break;
      case 'service':
        filtered = terminals.filter(t => t.terminalType === 'service');
        break;
      case 'development':
        filtered = terminals.filter(t => t.terminalType === 'development');
        break;
      case 'monitoring':
        filtered = terminals.filter(t => t.terminalType === 'monitoring');
        break;
      default:
        break;
    }

    return filtered;
  };

  const exportTerminalList = () => {
    const data = getFilteredTerminals().map(terminal => ({
      id: terminal.id,
      name: terminal.name || terminal.shell,
      type: terminal.terminalType,
      status: terminal.isActive ? 'active' : 'inactive',
      created: terminal.createdAt,
      lastActivity: terminal.lastActivity,
      connections: terminal.subscribers || 0
    }));

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `terminals-${workspaceId}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatUptime = (createdAt) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diff = now - created;
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const filteredTerminals = getFilteredTerminals();

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600">加载终端列表...</span>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            终端 ({terminals.length})
          </span>
          <button
            onClick={loadTerminals}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            <RefreshCw className="w-3 h-3" />
          </button>
        </div>
        
        <div className="space-y-1">
          {terminals.slice(0, 5).map((terminal) => (
            <div
              key={terminal.id}
              className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm cursor-pointer hover:bg-gray-100"
              onClick={() => onTerminalSelect && onTerminalSelect(terminal)}
            >
              <div className="flex items-center space-x-2">
                <TerminalTypeIndicator
                  terminalType={terminal.terminalType}
                  persistenceLevel={terminal.persistenceLevel}
                  compact={true}
                />
                <span className="truncate">{terminal.name || terminal.shell}</span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteTerminal(terminal.id);
                }}
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
        
        {terminals.length > 5 && (
          <div className="text-center">
            <button
              onClick={() => setShowBatchActions(true)}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              查看全部 {terminals.length} 个终端
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 头部工具栏 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold text-gray-800">
            终端管理 ({filteredTerminals.length}/{terminals.length})
          </h3>
          
          {selectedTerminals.size > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                已选择 {selectedTerminals.size} 个
              </span>
              <button
                onClick={() => handleBatchDelete(false)}
                className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
              >
                删除选中
              </button>
              <button
                onClick={() => handleBatchDelete(true)}
                className="px-2 py-1 text-xs bg-red-200 text-red-800 rounded hover:bg-red-300"
              >
                强制删除
              </button>
              <button
                onClick={clearSelection}
                className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                取消选择
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1"
          >
            <option value="all">全部终端</option>
            <option value="active">活跃终端</option>
            <option value="inactive">非活跃终端</option>
            <option value="service">服务终端</option>
            <option value="development">开发终端</option>
            <option value="monitoring">监控终端</option>
          </select>
          
          <button
            onClick={() => setShowStats(!showStats)}
            className="p-1 text-gray-600 hover:text-gray-800"
            title="资源统计"
          >
            <BarChart3 className="w-4 h-4" />
          </button>
          
          <button
            onClick={exportTerminalList}
            className="p-1 text-gray-600 hover:text-gray-800"
            title="导出列表"
          >
            <Download className="w-4 h-4" />
          </button>
          
          <button
            onClick={loadTerminals}
            className="p-1 text-gray-600 hover:text-gray-800"
            title="刷新"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 资源统计 */}
      {showStats && resourceStats && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-800 mb-3">资源统计</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-gray-600">总终端数</div>
              <div className="font-semibold">{resourceStats.totalTerminals}</div>
            </div>
            <div>
              <div className="text-gray-600">活跃连接</div>
              <div className="font-semibold">{resourceStats.totalConnections}</div>
            </div>
            <div>
              <div className="text-gray-600">缓冲区大小</div>
              <div className="font-semibold">
                {Math.round(resourceStats.memoryUsage.totalBufferSize / 1024)}KB
              </div>
            </div>
            <div>
              <div className="text-gray-600">历史命令</div>
              <div className="font-semibold">{resourceStats.memoryUsage.totalHistoryCommands}</div>
            </div>
          </div>
        </div>
      )}

      {/* 批量操作 */}
      {filteredTerminals.length > 0 && (
        <div className="flex items-center space-x-2 text-sm">
          <button
            onClick={selectAllTerminals}
            className="text-blue-600 hover:text-blue-800"
          >
            全选
          </button>
          <span className="text-gray-400">|</span>
          <button
            onClick={clearSelection}
            className="text-gray-600 hover:text-gray-800"
          >
            取消全选
          </button>
        </div>
      )}

      {/* 终端列表 */}
      <div className="space-y-2">
        {filteredTerminals.map((terminal) => (
          <div
            key={terminal.id}
            className={`border rounded-lg p-4 transition-colors ${
              selectedTerminals.has(terminal.id)
                ? 'border-blue-300 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={selectedTerminals.has(terminal.id)}
                  onChange={() => toggleTerminalSelection(terminal.id)}
                  className="rounded"
                />
                
                <div
                  className="flex-1 cursor-pointer"
                  onClick={() => onTerminalSelect && onTerminalSelect(terminal)}
                >
                  <div className="flex items-center space-x-2 mb-1">
                    <TerminalTypeIndicator
                      terminalType={terminal.terminalType}
                      persistenceLevel={terminal.persistenceLevel}
                      compact={true}
                    />
                    <span className="font-medium">
                      {terminal.name || terminal.shell}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded ${
                      terminal.isActive 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {terminal.isActive ? '活跃' : '非活跃'}
                    </span>
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    <span>运行时间: {formatUptime(terminal.createdAt)}</span>
                    <span className="mx-2">•</span>
                    <span>尺寸: {terminal.cols}x{terminal.rows}</span>
                    <span className="mx-2">•</span>
                    <span>连接: {terminal.subscribers || 0}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleDeleteTerminal(terminal.id, false)}
                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  title="删除终端"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                
                <button
                  onClick={() => handleDeleteTerminal(terminal.id, true)}
                  className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                  title="强制删除"
                >
                  <Square className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* 脚本执行监控 */}
            <div className="mt-3 border-t pt-3">
              <ScriptExecutionMonitor
                terminalId={terminal.id}
                compact={true}
              />
            </div>
          </div>
        ))}
      </div>

      {filteredTerminals.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          {filter === 'all' ? '暂无终端' : '没有符合条件的终端'}
        </div>
      )}
    </div>
  );
}
